'use client';

import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import React, { Fragment } from 'react';
import {
    ANIMATION_TICKS,
    getAnimationOptions,
    getSupportedAnimationGroups,
    normalizeAnimationGroup,
    normalizeAnimationPreset,
    snapDuration,
} from '../helpers/animation';

function keyLeaf(key) {
    return String(key)
        .replace(/\[\d+\]$/, '')
        .split('.')
        .pop();
}
function isAnimationKey(k) {
    const l = keyLeaf(k).toLowerCase();
    return l === 'animation' || l === 'animationpreset';
}
function isDurationKey(k) {
    return keyLeaf(k).toLowerCase() === 'duration';
}
function isAnimationGroupKey(k) {
    return keyLeaf(k).toLowerCase() === 'animationgroup';
}
function findKey(keys, pred) {
    return keys.find(pred) || null;
}

function Chip({ active, onClick, children }) {
    return (
        <span
            className={[
                'relative inline-flex rounded-xl p-[2px]',
                active
                    ? 'bg-primary-600'
                    : 'bg-neutral-300 hover:bg-gradient-to-br hover:from-primary-600 hover:to-secondary-600',
            ].join(' ')}
        >
            <button
                type="button"
                onClick={onClick}
                aria-pressed={active}
                className={[
                    'inline-flex h-9 items-center justify-center rounded-[10px] bg-white px-3 text-xs md:text-sm',
                    active
                        ? 'ring-2 ring-primary-600 ring-offset-1 ring-offset-white'
                        : 'ring-2 ring-neutral-200',
                    'text-neutral-700 shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-1',
                ].join(' ')}
            >
                {children}
            </button>
        </span>
    );
}

function Select({ value, onChange, options }) {
    const selected = options.find((o) => o.value === value) ?? options[0];
    return (
        <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
            <Listbox value={selected.value} onChange={onChange}>
                <div className="relative">
                    <Listbox.Button className="relative w-full cursor-default rounded-[10px] bg-white py-1.5 pl-3 pr-8 text-left text-xs leading-5 text-neutral-800 focus:outline-none md:text-sm">
                        <span>{selected.label}</span>
                        <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                            <ChevronDownIcon className="h-4 w-4 text-neutral-500" />
                        </span>
                    </Listbox.Button>
                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <Listbox.Options className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                            {options.map((opt) => (
                                <Listbox.Option
                                    key={opt.value}
                                    value={opt.value}
                                    className={({ active, selected }) =>
                                        [
                                            'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                            active
                                                ? 'bg-primary-50 text-primary-700'
                                                : 'text-neutral-800',
                                            selected ? 'font-medium' : '',
                                        ].join(' ')
                                    }
                                >
                                    {({ selected }) => (
                                        <>
                                            <span className="truncate">
                                                {opt.label}
                                            </span>
                                            {selected && (
                                                <CheckIcon className="h-4 w-4 text-primary-600" />
                                            )}
                                        </>
                                    )}
                                </Listbox.Option>
                            ))}
                        </Listbox.Options>
                    </Transition>
                </div>
            </Listbox>
        </div>
    );
}

function getFirstSelectableAnimation(group) {
    return (
        getAnimationOptions(group).find((option) => option.value !== 'none') ||
        getAnimationOptions(group)[0]
    )?.value;
}

export default function AnimationControls({
    component_props = {},
    file,
    onChange,
}) {
    const isFooter =
        typeof file === 'string' &&
        /(^|\/)Footer\.(jsx|js|tsx|ts)$/i.test(file);
    const isPopup =
        typeof file === 'string' && /(^|\/)Popup\.(jsx|js|tsx|ts)$/i.test(file);

    const keys = Object.keys(component_props || {});
    const hasAnimKey = keys.some((k) => isAnimationKey(k));
    const hasDurKey = keys.some((k) => isDurationKey(k));
    const hasGroupKey = keys.some((k) => isAnimationGroupKey(k));

    const supported = Array.from(
        new Set(getSupportedAnimationGroups(component_props))
    );

    const shouldRender =
        !isFooter &&
        !isPopup &&
        (hasAnimKey || hasDurKey || hasGroupKey || supported.length > 1);

    const animKey = findKey(keys, isAnimationKey) || 'Animation';
    const durKey = findKey(keys, isDurationKey) || 'Duration';
    const groupKey = findKey(keys, isAnimationGroupKey) || 'AnimationGroup';
    const group = normalizeAnimationGroup(
        component_props[groupKey],
        component_props
    );

    // Persist normalized group so the component uses the same value the UI shows.
    React.useEffect(() => {}, [group, file, onChange, groupKey]);

    const preset = normalizeAnimationPreset(component_props[animKey], group);
    const rawPreset = String(component_props[animKey] ?? '')
        .toLowerCase()
        .trim();

    React.useEffect(() => {
        if (!shouldRender) return;
        if (
            group !== 'features' &&
            (preset === 'stagger' ||
                rawPreset === 'stagger' ||
                rawPreset === 'cascade')
        ) {
            onChange?.({
                file,
                key: animKey,
                value: getFirstSelectableAnimation(group) || 'fade',
            });
        }
    }, [shouldRender, preset, rawPreset, group, animKey, file, onChange]);

    const options = getAnimationOptions(group);

    const curSeconds =
        typeof component_props[durKey] === 'number'
            ? component_props[durKey]
            : Number.parseFloat(component_props[durKey]) || 0;
    const snapped = snapDuration(curSeconds);
    const [draft, setDraft] = React.useState(String(snapped));
    const [idx, setIdx] = React.useState(ANIMATION_TICKS.indexOf(snapped));

    React.useEffect(() => {
        const s = snapDuration(curSeconds);
        setDraft(String(s));
        setIdx(ANIMATION_TICKS.indexOf(s));
    }, [curSeconds]);

    if (!shouldRender) {
        return null;
    }

    const commit = (seconds) => {
        const s = snapDuration(seconds);
        setDraft(String(s));
        setIdx(ANIMATION_TICKS.indexOf(s));
        onChange?.({ file, key: durKey, value: s });
    };

    const handleGroupChange = (nextGroup) => {
        onChange?.({ file, key: groupKey, value: nextGroup });

        const nextOptions = getAnimationOptions(nextGroup);
        const nextPreset = normalizeAnimationPreset(
            component_props[animKey],
            nextGroup
        );
        const isCascadePreset =
            rawPreset === 'stagger' || rawPreset === 'cascade';
        const supportsPreset =
            nextOptions.some((option) => option.value === nextPreset) &&
            !(isCascadePreset && nextGroup !== 'features');

        if (!supportsPreset) {
            onChange?.({
                file,
                key: animKey,
                value: getFirstSelectableAnimation(nextGroup) || 'fade',
            });
        }
    };

    return (
        <div className="mb-4">
            <label className="block text-xs font-semibold leading-5 text-primary-700">
                Animation Group
            </label>
            <div className="mt-1 inline-flex flex-wrap gap-2">
                {supported.map((v) => (
                    // Map plural key to readable label (e.g., 'medias' -> 'Media')
                    <Chip
                        key={v}
                        active={group === v}
                        onClick={() => handleGroupChange(v)}
                    >
                        {v === 'ctas'
                            ? 'CTAs'
                            : v === 'medias'
                            ? 'Media'
                            : v[0].toUpperCase() + v.slice(1)}
                    </Chip>
                ))}
            </div>

            <div className="mt-4 flex items-end gap-3">
                <div className="flex-1">
                    <label className="block text-xs font-semibold leading-5 text-primary-700">
                        Animation
                    </label>
                    <div className="mt-1">
                        <Select
                            value={preset}
                            onChange={(val) =>
                                onChange?.({ file, key: animKey, value: val })
                            }
                            options={options}
                        />
                    </div>
                </div>

                <div className="w-2/4 max-w-full">
                    <label className="block text-xs font-semibold leading-5 text-primary-700">
                        Duration
                    </label>
                    <div className="mt-1 grid h-9 w-full grid-cols-[minmax(0,1fr)_35%] items-center gap-0.5">
                        <input
                            type="range"
                            min={0}
                            max={ANIMATION_TICKS.length - 1}
                            step={1}
                            value={idx}
                            onChange={(e) =>
                                commit(
                                    ANIMATION_TICKS[Number(e.target.value) || 0]
                                )
                            }
                            className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-neutral-200 accent-primary-600"
                            aria-label="Duration slider"
                        />
                        <input
                            type="text"
                            inputMode="decimal"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onBlur={() => {
                                const n = Number.parseFloat(
                                    (draft || '').replace(',', '.')
                                );
                                commit(Number.isFinite(n) ? n : 0);
                            }}
                            className="w-full border-0 bg-transparent py-1 text-center text-xs leading-4 text-neutral-800"
                            aria-label="Duration seconds"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
